import { Grid, Paper, CircularProgress, Typography, Divider, Box, Button } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getMemberById, getMemberSettings } from 'src/shared/services/memberService';
import { useTranslation } from 'react-i18next';
import { Edit, Email, Phone } from '@mui/icons-material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { Member } from '../models/member';
import MemberAvatar from '../components/memberAvatar';
import { MemberEditModal } from '../components/memberEditModal';
import { MembershipPartial } from '../components/membershipPartial';
import { MemberDetailGender } from '../components/memberDetailGender';
import { MemberDetailBirthDate } from '../components/memberDetailBirthDate';
import { MemberStatuses } from '../components/memberStatuses';
import { MemberDetail } from '../components/memberDetail';
import { MemberDetailLink } from '../components/memberDetailLink';
import MemberInvoices from '../components/memberInvoices';
import { MemberUpdateRequestedMessage } from '../components/memberUpdateRequestedMessage';
import MemberMeInvoiceAddress from '../components/memberMeInvoiceAddress';
import { MemberTitleSummary } from '../components/memberTitleSummary';
import { MemberEmailLog } from '../components/memberEmailLog';

export const MemberMeDetailsPage = () => {
  const { id } = useParams();

  const { t } = useTranslation(['member', 'common']);
  const { currentMember, dateStamp, setDateStamp } = useGlobals();

  const isMounted = useIsMounted();
  const [loading, setIsLoading] = useState(false);
  const [member, setMember] = useState<Member>();
  const [editMember, setEditMember] = useState<boolean>(false);

  const onActivateEditMember = () => {
    setEditMember(true);
  };

  const fetchData = useCallback(
    async (uid: string | number) => {
      setIsLoading(true);
      try {
        const result = await getMemberById(uid);
        if (isMounted()) {
          setMember(result.data);
          ConsoleHelper.log('RES member me', result.data, dateStamp);
        }
      } catch (err) {
        if (isMounted()) {
          ConsoleHelper.log(err);
        }
      } finally {
        if (isMounted()) {
          setIsLoading(false);
        }
      }
    },
    [isMounted, dateStamp],
  );

  const fetchMember = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getMemberSettings();
      if (isMounted()) {
        setMember(result.data);
        ConsoleHelper.log('RES member me', result.data, dateStamp);
      }
    } catch (err) {
      if (isMounted()) {
        ConsoleHelper.log(err);
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, [dateStamp, isMounted]);

  useEffect(() => {
    if (currentMember) {
      if (dateStamp) {
        fetchMember();
      } else {
        setMember(currentMember);
      }
    } else if (id !== undefined) fetchData(id);
  }, [id, fetchData, dateStamp, currentMember, fetchMember]);

  const displayOtherDetails = (label: string, value: string | undefined, key: string | number) => {
    return (
      value && (
        <Grid item xs='auto' zeroMinWidth sx={{ maxWidth: '100%' }} key={key}>
          <Typography variant='body1' component='div' sx={{ fontWeight: 500 }}>
            {label.replace('<BR>', '').replace('<br>', '')}
          </Typography>
          <Typography variant='body1' component='div'>
            {value}
          </Typography>
        </Grid>
      )
    );
  };
  const displayUnlockedProfileDetails = (label: string, value: string | undefined, key: string | number) => {
    return (
      value && (
        <Grid item xs='auto' zeroMinWidth sx={{ maxWidth: '100%' }} key={key}>
          <Typography variant='body1' component='div' sx={{ fontWeight: 500 }}>
            {label.replace('<BR>', '').replace('<br>', '')}
          </Typography>
          <Typography variant='body1' component='div'>
            {value}
          </Typography>
        </Grid>
      )
    );
  };
  const displayTextDetails = (label: string, value: string | undefined) => {
    return (
      value && (
        <Grid item xs={6} zeroMinWidth sx={{ maxWidth: '50%' }} key={label}>
          <Typography variant='body1' component='div' sx={{ fontWeight: 500 }}>
            {label.replace('<BR>', '').replace('<br>', '')}
          </Typography>
          <Typography variant='body1' component='div'>
            {value}
          </Typography>
        </Grid>
      )
    );
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs sx={{ display: 'flex', alignItems: 'center' }}>
          <MainContentHeader title={t('member:memberDetails.header', { memberNr: member?.memberNr })} />
          {member && (
            <Box sx={{ ml: 1 }}>
              <MemberStatuses status={member.status} resDate={member.resDate} chipSize='small' />
            </Box>
          )}
        </Grid>
        <Grid item xs='auto' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!loading && member && (
            <Button
              size='small'
              variant='outlined'
              onClick={onActivateEditMember}
              endIcon={<Edit fontSize='small' />}
              sx={{ ml: '1rem' }}
              disabled={member.memberUpdate_id !== null} // Låst pga begärd uppdatering
            >
              {t('common:button.edit')}
            </Button>
          )}
        </Grid>
        {!loading && member && member.memberUpdate_id !== null && (
          <MemberUpdateRequestedMessage memberUpdateId={member.memberUpdate_id} isMember={!!currentMember} callback={() => ConsoleHelper.log('')} />
        )}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, overflow: 'hidden' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
              </Box>
            )}
            {!loading && member && (
              <GridWrapper>
                <Grid item xs={12} lg={8}>
                  <GridWrapper>
                    <Grid item xs={12}>
                      <ContentSection>
                        <GridWrapper>
                          <Grid item xs='auto'>
                            <MemberAvatar firstName={member.firstName ?? ''} lastName={member.lastName ?? ''} />
                          </Grid>
                          <Grid item xs='auto'>
                            <Typography variant='h6' sx={{ fontWeight: 700 }} gutterBottom>{`${member.firstName} ${member.lastName}`}</Typography>
                            <MemberDetail detail={member.coAddress ? `${t('member:tableHeaders.COAddress')} ${member.coAddress}` : undefined} />
                            <MemberDetail detail={member.address} />
                            <MemberDetail detail={member.addressOther} />
                            <MemberDetail detail={member.postNr} />
                            <MemberDetail detail={member.postAddress} />
                            <MemberDetail detail={member.country} />
                          </Grid>
                          <Grid item>
                            <Typography variant='h6' gutterBottom>
                              &nbsp;
                            </Typography>
                            <MemberDetailLink icon={<Email sx={{ mr: 1 }} />} linkPrefix='mailto:' details={[member.email || '']} />
                            <MemberDetailLink
                              icon={<Phone sx={{ mr: 1 }} />}
                              linkPrefix='tel:'
                              details={[member.phone || '', member.phoneOther || '']}
                            />
                            <MemberDetailBirthDate birthDate={member.birthDate} />
                            <MemberDetailGender sex={member.sex} />
                          </Grid>
                        </GridWrapper>
                        {((member.customData && member.customData?.filter((x) => !!x.value).length > 0) ||
                          (member.profileData && member.profileData?.filter((x) => x.locked).length > 0)) && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <GridWrapper>
                              {member.customData?.filter((x) => !!x.value).map((item) => displayOtherDetails(item.heading, item.value, item.key))}
                              {member.profileData
                                ?.filter((x) => x.locked)
                                .map((item) => displayOtherDetails(item.groupName, item.itemName, item.memberProfileItem_id))}
                            </GridWrapper>
                          </>
                        )}
                        {member.profileData && member.profileData?.filter((x) => !x.locked).length > 0 && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <GridWrapper>
                              {member.profileData
                                ?.filter((x) => !x.locked)
                                .map((item) => displayUnlockedProfileDetails(item.groupName, item.itemName, item.memberProfileItem_id))}
                            </GridWrapper>
                          </>
                        )}
                        {(member.comment || member.note) && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <GridWrapper>
                              {displayTextDetails(t('member:memberDetails.comment'), member.comment)}
                              {displayTextDetails(t('member:memberDetails.note'), member.note)}
                            </GridWrapper>
                          </>
                        )}
                      </ContentSection>
                    </Grid>
                    <Grid item xs={12}>
                      {member && <MemberMeInvoiceAddress member={member} />}
                    </Grid>
                    <Grid item xs={12}>
                      {member && <MemberInvoices otherContactMode={false} member={member} />}
                    </Grid>
                    {member && member.titles && member.titles.length > 0 && (
                      <Grid item xs={12}>
                        <MemberTitleSummary member={member} />
                      </Grid>
                    )}
                  </GridWrapper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  {member && (
                    <MembershipPartial
                      otherContactMode={false}
                      member={member}
                      refetchMemberCallback={() => id && fetchData(id)}
                      refetchMemberMeCallback={() => fetchMember()}
                    />
                  )}
                </Grid>
              </GridWrapper>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
      {member && editMember && (
        <MemberEditModal
          otherContactMode={false}
          member={member}
          onClose={() => {
            setEditMember(false);
            setDateStamp(new Date());
          }}
          callback={() => {
            if (member.member_id) {
              fetchData(member.member_id.toString());
            }
          }}
          onSave={() => {
            setEditMember(false);
            setDateStamp(new Date());
            if (member.member_id) {
              fetchData(member.member_id.toString());
            }
          }}
          modalOpen={editMember}
        />
      )}
    </>
  );
};
